import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type UserRole = 'ADMIN' | 'OPERATOR' | 'FINANCIAL'

export interface TeamMember {
  id: string
  name: string
  email: string
  role: UserRole
  tenantRoleId: string | null
  tenantRole: { id: string; name: string; preset: UserRole | null } | null
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface PaginatedTeam {
  data: TeamMember[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export interface PasswordResetResult {
  token: string
  expiresAt: string
  resetUrl: string
}

export interface ListTeamParams {
  search?: string
  role?: UserRole
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListTeamParams) => ['team', 'list', params] as const,
}

export function useTeam(params: ListTeamParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => api.get<PaginatedTeam>('/users', { params }).then((r) => r.data),
  })
}

export function useUpdateUserRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, roleId }: { id: string; roleId: string }) =>
      api.patch<TeamMember>(`/users/${id}/role`, { roleId }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useDeactivateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<TeamMember>(`/users/${id}/deactivate`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useReactivateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<TeamMember>(`/users/${id}/reactivate`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useGeneratePasswordReset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<PasswordResetResult>(`/users/${id}/password-reset`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audit-logs'] }),
  })
}
